import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  type OpportunityStage,
  type CreateOpportunityStageParams,
  type UpdateOpportunityStageParams,
  ListOpportunityStagesResponseSchema,
  CreateOpportunityStageResponseSchema,
  UpdateOpportunityStageResponseSchema,
} from "@forgepoint/shared";

interface OpportunityStagesStore {
  /** Map of stage ID to stage object. */
  stages: Map<string, OpportunityStage>;
  /** Ordered list of non-archived stage IDs, sorted by sortOrder. */
  orderedStageIds: string[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;

  fetchStages: () => Promise<void>;
  createStage: (
    params: CreateOpportunityStageParams,
  ) => Promise<{ stage: OpportunityStage | null; error: string | null }>;
  updateStage: (
    id: string,
    params: UpdateOpportunityStageParams,
  ) => Promise<{ stage: OpportunityStage | null; error: string | null }>;
  /** Returns active (non-archived) stages in sortOrder. */
  getActiveStages: () => OpportunityStage[];
  /** Returns a stage by ID. */
  getStage: (id: string) => OpportunityStage | undefined;
  /** Returns the first default stage (one of the seeded defaults). */
  getDefaultStage: () => OpportunityStage | undefined;
}

/**
 * Derives the ordered list of active (non-archived) stage IDs from a stages map.
 * @param stages - Map of stage ID to stage object
 * @returns Sorted array of active stage IDs
 */
function deriveOrderedStageIds(
  stages: Map<string, OpportunityStage>,
): string[] {
  return Array.from(stages.values())
    .filter((s) => !s.archivedAt)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((s) => s.id);
}

/**
 * Zustand store for organization-level opportunity stages.
 * Stages are fetched once and shared across kanban, editor, and detail sheets.
 */
export const useOpportunityStagesStore = create<OpportunityStagesStore>(
  (set, get) => ({
    stages: new Map(),
    orderedStageIds: [],
    isLoading: false,
    error: null,
    hasFetched: false,

    fetchStages: async () => {
      set({ isLoading: true, error: null });
      try {
        const response = await fetchApi("opportunity-stages", "GET");
        if (response.ok && response.data) {
          const data = ListOpportunityStagesResponseSchema.parse(response.data);
          const stagesMap = new Map<string, OpportunityStage>();
          data.stages.forEach((stage) => stagesMap.set(stage.id, stage));
          set({
            stages: stagesMap,
            orderedStageIds: deriveOrderedStageIds(stagesMap),
            isLoading: false,
            hasFetched: true,
          });
          return;
        }
        set({
          error: response.error ?? "Failed to fetch stages",
          isLoading: false,
          hasFetched: true,
        });
      } catch (error) {
        captureException(error as Error);
        set({
          error: "Failed to fetch stages",
          isLoading: false,
          hasFetched: true,
        });
      }
    },

    createStage: async (params) => {
      try {
        const response = await fetchApi("opportunity-stages", "POST", {
          body: params,
        });
        if (response.ok && response.data) {
          const result = CreateOpportunityStageResponseSchema.parse(
            response.data,
          );
          const stagesMap = new Map(get().stages);
          stagesMap.set(result.stage.id, result.stage);
          set({
            stages: stagesMap,
            orderedStageIds: deriveOrderedStageIds(stagesMap),
          });
          return { stage: result.stage, error: null };
        }
        return {
          stage: null,
          error: response.error ?? "Failed to create stage",
        };
      } catch (error) {
        captureException(error as Error);
        return { stage: null, error: "Failed to create stage" };
      }
    },

    updateStage: async (id, params) => {
      try {
        const response = await fetchApi(`opportunity-stages/${id}`, "PUT", {
          body: params,
        });
        if (response.ok && response.data) {
          const result = UpdateOpportunityStageResponseSchema.parse(
            response.data,
          );
          const stagesMap = new Map(get().stages);
          stagesMap.set(id, result.stage);
          set({
            stages: stagesMap,
            orderedStageIds: deriveOrderedStageIds(stagesMap),
          });
          return { stage: result.stage, error: null };
        }
        return {
          stage: null,
          error: response.error ?? "Failed to update stage",
        };
      } catch (error) {
        captureException(error as Error);
        return { stage: null, error: "Failed to update stage" };
      }
    },

    getActiveStages: () => {
      const { stages, orderedStageIds } = get();
      return orderedStageIds
        .map((id) => stages.get(id))
        .filter((s): s is OpportunityStage => s !== undefined);
    },

    getStage: (id) => get().stages.get(id),

    getDefaultStage: () =>
      Array.from(get().stages.values()).find(
        (s) => s.isDefault && !s.archivedAt,
      ),
  }),
);
